import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home as HomeIcon, FileText, Star, ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';
import axios from 'axios';
import { siteConfig } from '@/config/siteConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [openHouses, setOpenHouses] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [agentPhotoUrl, setAgentPhotoUrl] = useState('');

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      }
    };
    
    const fetchOpenHouses = async () => {
      try {
        const response = await axios.get(`${API}/open-houses?active_only=true`);
        setOpenHouses(response.data);
      } catch (error) {
        console.error('Failed to fetch open houses:', error);
      }
    };

    const fetchAgentPhoto = async () => {
      try {
        const response = await axios.get(`${API}/agent-photo`);
        if (response.data.photo_url) {
          setAgentPhotoUrl(response.data.photo_url);
        }
      } catch (error) {
        console.error('Failed to fetch agent photo:', error);
      }
    };
    
    fetchTestimonials();
    fetchOpenHouses();
    fetchAgentPhoto();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (openHouses.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % openHouses.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [openHouses.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % openHouses.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + openHouses.length) % openHouses.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 pt-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/90 to-[#1e3a8a]/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-testid="hero-section">
          {agentPhotoUrl && (
            <div className="flex justify-center mb-6">
              <img
                src={agentPhotoUrl}
                alt={siteConfig.agentName}
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover object-top border-4 border-[#d4af37] shadow-2xl"
              />
            </div>
          )}
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Find Your Dream Home
            <br />
            <span className="text-[#d4af37]">with {siteConfig.agentName}</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {siteConfig.tagline} serving California and Florida. Let me guide you home.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="hero-portfolio-app-btn"
              onClick={() => navigate('/portfolio')}
              size="lg"
              className="relative overflow-hidden group bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white text-lg px-8 py-6 border-2 border-transparent hover:border-white/20 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-emerald-500/20 hover:scale-[1.02]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 relative z-10">
                <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
                <line x1="16" x2="8" y1="21" y2="21" />
                <line x1="12" x2="12" y1="17" y2="21" />
              </svg>
              <span className="relative z-10 font-semibold">Portfolio App</span>
            </Button>
            <Button
              data-testid="hero-view-properties-btn"
              onClick={() => navigate('/properties')}
              size="lg"
              className="bg-white text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white text-lg px-8 py-6 border-2 border-white transition-colors duration-200 shadow-md"
            >
              <div className="flex items-center justify-center">
                <HomeIcon className="mr-2 h-5 w-5" />
                <span className="font-medium">View Properties</span>
              </div>
            </Button>
            <Button
              data-testid="hero-get-started-btn"
              onClick={() => navigate('/signup')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1e3a8a] text-lg px-8 py-6"
            >
              <FileText className="mr-2 h-5 w-5" />
              Get Started
            </Button>
            <Button
              data-testid="hero-leave-review-btn"
              onClick={() => navigate('/leave-review')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#1e3a8a] text-lg px-8 py-6"
            >
              <Star className="mr-2 h-5 w-5" />
              Leave Review
            </Button>
          </div>
        </div>
      </section>

      {/* Open Houses Carousel - Only show if there are active open houses */}
      {openHouses.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="open-houses-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Upcoming Open Houses
              </h2>
              <p className="text-lg text-slate-300">
                Join us at our featured properties
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              {/* Carousel Container */}
              <div className="relative overflow-hidden rounded-xl shadow-2xl bg-white">
                {/* Current Slide */}
                {openHouses.map((house, index) => (
                  <div
                    key={house.id}
                    className={`transition-opacity duration-500 ${
                      index === currentSlide ? 'block' : 'hidden'
                    }`}
                    data-testid={`open-house-slide-${index}`}
                  >
                    {/* Image/Video Display */}
                    <div className="relative h-96 bg-slate-200">
                      {house.flyer_url ? (
                        <img
                          src={house.flyer_url}
                          alt={`${house.address} - Open House Flyer`}
                          className="w-full h-full object-contain bg-white"
                          onError={(e) => {
                            // Fallback to first photo if flyer fails to load
                            if (house.photos && house.photos.length > 0) {
                              e.target.src = house.photos[0];
                              e.target.className = "w-full h-full object-cover";
                            }
                          }}
                        />
                      ) : house.photos && house.photos.length > 0 ? (
                        <img
                          src={house.photos[0]}
                          alt={house.address}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <HomeIcon className="h-24 w-24 text-slate-400" />
                        </div>
                      )}
                      
                      {/* Navigation Arrows - Only show if more than one slide */}
                      {openHouses.length > 1 && (
                        <>
                          <button
                            onClick={prevSlide}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                            data-testid="carousel-prev-btn"
                            aria-label="Previous slide"
                          >
                            <ChevronLeft className="h-6 w-6 text-slate-800" />
                          </button>
                          <button
                            onClick={nextSlide}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                            data-testid="carousel-next-btn"
                            aria-label="Next slide"
                          >
                            <ChevronRight className="h-6 w-6 text-slate-800" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-[#1e3a8a]" style={{ fontFamily: 'Playfair Display, serif' }}>
                          <MapPin className="inline h-6 w-6 mr-2 text-[#d4af37]" />
                          {house.address}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-slate-600 mb-4">
                        <Calendar className="h-5 w-5 mr-2 text-[#d4af37]" />
                        <span className="font-semibold">
                          {new Date(house.open_house_date).toLocaleString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>

                      {house.description && (
                        <p className="text-slate-600 mb-6 leading-relaxed">
                          {house.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4">
                        {house.flyer_url && (
                          <Button
                            onClick={() => window.open(house.flyer_url, '_blank')}
                            className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
                          >
                            View Flyer
                          </Button>
                        )}
                        {house.mls_listing_url && (
                          <Button
                            onClick={() => window.open(house.mls_listing_url, '_blank')}
                            variant="outline"
                            className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                          >
                            MLS Listing
                          </Button>
                        )}
                        <Button
                          onClick={() => navigate('/signup')}
                          variant="outline"
                          className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-white"
                        >
                          Contact for Details
                        </Button>
                        {house.videos && house.videos.length > 0 && (
                          <Button
                            onClick={() => window.open(house.videos[0], '_blank')}
                            variant="outline"
                            className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                          >
                            View Video
                          </Button>
                        )}
                      </div>

                      {/* Additional Images Preview */}
                      {house.photos && house.photos.length > 1 && (
                        <div className="mt-6 grid grid-cols-4 gap-2">
                          {house.photos.slice(1, 5).map((photo, idx) => (
                            <img
                              key={idx}
                              src={photo}
                              alt={`${house.address} - ${idx + 2}`}
                              className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                              onClick={() => window.open(photo, '_blank')}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Slide Indicators - Only show if more than one slide */}
              {openHouses.length > 1 && (
                <div className="flex justify-center mt-6 gap-2">
                  {openHouses.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-3 rounded-full transition-all ${
                        index === currentSlide
                          ? 'w-8 bg-[#d4af37]'
                          : 'w-3 bg-white/50 hover:bg-white/75'
                      }`}
                      data-testid={`carousel-indicator-${index}`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* About Preview Section */}
      <section className="py-20 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={agentPhotoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80"}
                alt={`${siteConfig.agentName} - Professional Realtor`}
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
                style={{ objectPosition: 'center 20%' }}
                data-testid="about-preview-image"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                About {siteConfig.agentName}
              </h2>
              <p className="text-lg text-slate-600 mb-4">
                With over a decade of experience in real estate, I specialize in helping families and individuals find their perfect home in California and Florida.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                My commitment to personalized service and deep market knowledge ensures you'll have a trusted partner throughout your home buying journey.
              </p>
              <div className="space-y-2 text-slate-700 mb-6">
                <p><strong>California DRE#:</strong> 02251457</p>
                <p><strong>Florida License#:</strong> BK9876543</p>
              </div>
              <Button
                data-testid="about-learn-more-btn"
                onClick={() => navigate('/about')}
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
              >
                Learn More About Me
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Client Testimonials
            </h2>
            <p className="text-lg text-slate-600">
              Hear what my clients have to say about their experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-xl transition-shadow" data-testid="testimonial-card">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-[#d4af37] text-[#d4af37]" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.content}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-[#1e3a8a]">{testimonial.client_name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Had a great experience? Share your story!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="leave-review-link"
                onClick={() => navigate('/leave-review')}
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
              >
                <Star className="mr-2 h-5 w-5" />
                Leave a Review
              </Button>
              <Button
                data-testid="admin-testimonials-link"
                onClick={() => navigate('/admin/testimonials')}
                variant="outline"
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Admin Panel
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a8a] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Find Your Dream Home?
          </h2>
          <p className="text-xl mb-8">
            Let's start your home buying journey today. Fill out our quick form and I'll be in touch within 24 hours.
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={() => navigate('/signup')}
            size="lg"
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#1e3a8a] text-lg px-8 py-6 font-semibold"
          >
            Get Started Now
          </Button>
        </div>
      </section>
    </div>
  );
}
